import { useEffect, useRef } from 'react'
import { useAuth } from '@/context/AuthContext'
import { useCart } from '@/context/CartContext'
import { useFavorites } from '@/context/FavoritesContext'
import { cartApi } from '@/api/cart'

const CART_STORAGE_KEY = 'nurm_cart_v1'
const FAVORITES_STORAGE_KEY = 'nurm_favorites_v1'

function clearGuestCartFavoritesStorage() {
  localStorage.removeItem(CART_STORAGE_KEY)
  localStorage.removeItem(FAVORITES_STORAGE_KEY)
}

function loadGuestCart(): Array<{
  product_id: number
  quantity: number
  variant_id?: number
}> {
  try {
    const raw = localStorage.getItem(CART_STORAGE_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw) as Array<{
      id: string
      productId?: number
      quantity?: number
      variantId?: number
    }>
    if (!Array.isArray(parsed)) return []
    return parsed
      .map((it) => {
        const product_id =
          it.productId ??
          (() => {
            const n = Number(it.id)
            if (!Number.isNaN(n)) return n
            const [p] = it.id.split('_')
            return Number(p) || 0
          })()
        const quantity = Math.max(1, it.quantity ?? 1)
        const variant_id = it.variantId
        return variant_id != null
          ? { product_id, quantity, variant_id }
          : { product_id, quantity }
      })
      .filter((it) => !Number.isNaN(it.product_id) && it.product_id > 0)
  } catch {
    return []
  }
}

function loadGuestFavorites(): number[] {
  try {
    const raw = localStorage.getItem(FAVORITES_STORAGE_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw) as unknown
    if (!Array.isArray(parsed)) return []
    return parsed
      .map((x) => Number(x))
      .filter((id) => !Number.isNaN(id) && id > 0)
  } catch {
    return []
  }
}

/**
 * После входа читает гостевую корзину и избранное из localStorage, отправляет на бэкенд (sync),
 * затем перезагружает корзину и избранное с сервера.
 */
export function SyncCartFavoritesOnLogin() {
  const { isAuthenticated, token } = useAuth()
  const { reloadFromServer: reloadCart } = useCart()
  const { reloadFromServer: reloadFavorites } = useFavorites()
  const hasSynced = useRef(false)

  useEffect(() => {
    if (!isAuthenticated || !token) {
      hasSynced.current = false
      return
    }
    if (hasSynced.current) return
    hasSynced.current = true

    const cartPayload = loadGuestCart()
    const favoritePayload = loadGuestFavorites()

    cartApi
      .syncCartFavorites({
        cart: cartPayload,
        favorite_ids: favoritePayload,
      })
      .then(() => {
        clearGuestCartFavoritesStorage()
        reloadCart()
        reloadFavorites()
      })
      .catch(() => {
        reloadCart()
        reloadFavorites()
      })
  }, [isAuthenticated, token, reloadCart, reloadFavorites])

  return null
}
